import {
  Alert,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { PollResponse, User, Voter, VoterPreferenceFilters } from '../../../types';
import React, { useEffect, useState } from 'react';
import { Timestamp, collection, getDocs, orderBy, query, where } from 'firebase/firestore';

import Papa from 'papaparse';
import { db } from '../../../firebase';
import useAuth from '../../../hooks/useAuth';

interface VoterPreferencePrecinctReportProps {
  filters: VoterPreferenceFilters
}

// Helper function to convert response value to a sortable string
const getStringValue = (value: string | number | string[] | null | undefined): string => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
};

// Helper function to convert Timestamp to readable string
const formatTimestamp = (timestamp: any): string => {
  if (!timestamp) return '';
  try {
    return timestamp.toDate ? timestamp.toDate().toLocaleString() : String(timestamp);
  } catch {
    return String(timestamp);
  }
};

// Updated helper function to extract and sort selected candidates
const getSelectedCandidates = (responses: Record<string, any>): string[] => {
  const candidates = Object.entries(responses)
    .filter(([key, value]) => 
      // Exclude non-candidate responses like 'VOTER'
      key !== 'VOTER' && key !== 'PARTY' && key !== 'ORGANIZATIONS' &&
      // Check if the value is truthy (selected)
      value
    )
    .map(([key, value]) => value);
  return candidates.sort((a, b) => a.localeCompare(b)); // Sort candidates alphabetically
};

interface VoterResponse {
  voter: Voter;
  pollResponse: PollResponse;
}

const VoterPreferencePrecinctReport: React.FC<VoterPreferencePrecinctReportProps> = ({ filters }) => {
  const { user } = useAuth();
  
  const [voterResponses, setVoterResponses] = useState<VoterResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchVotersAndPolls = async (user: User) => {
      try {
        setLoading(true);

        const votersRef = collection(db, "voters");
        let q = query(votersRef,
          orderBy("name"),
          where("province", "==", filters.province),
          where("city_municipality", "==", filters.municipality),
          where("barangay", "==", filters.barangay)
        );
        if (filters.precinct) {
          q = query(q, where("precinct", "==", filters.precinct));
        }
        const querySnapshot = await getDocs(q);
        const _voterResponses: VoterResponse[] = querySnapshot.docs.map(doc => ({
          voter: doc.data() as Voter, 
          pollResponse: {
            id: '',
            pollId: '',
            pollTitle: '',
            responses:  { 
            },
            submittedBy: '',
            submittedAt: Timestamp.now(),
            validated: false,
          } as PollResponse
        }));
        
        // Base collection path for barangay
        const barangayCollectionPath = `tabulationsV2/${user.dashboard}/regions/${filters.region}/provinces/${filters.province}/districts/${filters.district}/cities/${filters.municipality}/barangays/${filters.barangay}/precincts`;
        
        // Fetch precincts in the barangay
        const precinctRef = collection(db, barangayCollectionPath);
        const precinctSnapshot = await getDocs(precinctRef);

        // Iterate through precincts (or just the specified precinct)
        for (const precinctDoc of precinctSnapshot.docs) {
          // Skip if a specific precinct is selected and this isn't it
          if (filters.precinct && precinctDoc.id !== filters.precinct) continue;

          const votersRef = collection(precinctDoc.ref, "voters");

          const votersSnapshot = await getDocs(votersRef);
          for (const latestPollDoc of votersSnapshot.docs) {
            const pr = latestPollDoc.data() as PollResponse;
            // replace the pollResponse in the voterResponses array
            const voterResponse = _voterResponses.find(vr => vr.voter.name === pr.responses['VOTER']);
            if (voterResponse) {
              voterResponse.pollResponse = pr;
            } 
          }
        }
        
        // Filter by candidate filters if specified
        const filteredVoterResponses = filters.candidateFilters.length > 0
          ? _voterResponses.filter(vr => {
              // Check if the response matches ALL candidate filters
              return filters.candidateFilters.every(filter => {
                const selectedCandidates = getSelectedCandidates(vr.pollResponse.responses);
                
                // Check if the response contains the candidate
                const candidateMatch = selectedCandidates.some(candidate => 
                  filter.candidate === candidate
                );

                // Determine match based on matchType
                return filter.matchType === 'match' 
                  ? candidateMatch 
                  : !candidateMatch;
              });
            })
          : _voterResponses;
        
        setVoterResponses(filteredVoterResponses);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching voters and polls:', err);
        setError('Failed to fetch voters and polls');
        setLoading(false);
      }
    };

    if (user && filters.region && filters.province &&
      filters.district && filters.municipality && filters.barangay
    ) {
      fetchVotersAndPolls(user);
    }
  }, [user, filters]);

  // Updated export to CSV function to include selected candidates
  const exportToCSV = () => {
    // Prepare CSV data
    const csvData = voterResponses.map((voterResponse, index) => ({
      '#': index + 1,
      'Voter Name': getStringValue(voterResponse.pollResponse.responses['VOTER']),
      'Precinct': voterResponse.voter.precinct,
      'Selected Candidates': voterResponse.pollResponse.id ? getSelectedCandidates(voterResponse.pollResponse.responses).join(', ') : 'undecided',
      'Polled By': voterResponse.pollResponse.submittedBy,
      'Submitted At': voterResponse.pollResponse.submittedBy ? formatTimestamp(voterResponse.pollResponse.submittedAt) : ''
    }));

    // Generate CSV file
    const csv = Papa.unparse(csvData);
    
    // Create and download CSV file
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `voters_preference_${filters.barangay}${filters.precinct ? '_precinct_' + filters.precinct : ''}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <Grid container spacing={2} sx={{ mt: 2, justifyContent: 'center' }}>
        <Grid item>
          <CircularProgress />
        </Grid>
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <Alert severity="error">{error}</Alert>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={2} sx={{ mt: 2 }}>
      <Grid item xs={12}>
        <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Grid item>
            <Typography variant="h6" gutterBottom>
              Poll Responses in {filters.barangay}
              {filters.precinct ? ` - Precinct ${filters.precinct}` : ' (All Precincts)'}
            </Typography>
          </Grid>
          <Grid item>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={exportToCSV}
              disabled={voterResponses.length === 0}
            >
              Export to CSV
            </Button>
          </Grid>
        </Grid>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Voter Name</TableCell>
                <TableCell>Precinct</TableCell>
                <TableCell>Selected Candidates</TableCell>
                <TableCell>Polled By</TableCell>
                <TableCell>Submitted At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {voterResponses.map((voterResponse, index) => (
                <TableRow key={voterResponse.voter.id}>
                  <TableCell>{++index}</TableCell>
                  <TableCell>{getStringValue(voterResponse.voter.name)}</TableCell>
                  <TableCell>{voterResponse.voter.precinct}</TableCell>
                  <TableCell>{voterResponse.pollResponse.id ? getSelectedCandidates(voterResponse.pollResponse.responses).join(', ') : 'undecided'}</TableCell>
                  <TableCell>{voterResponse.pollResponse.submittedBy}</TableCell>
                  <TableCell>{voterResponse.pollResponse.submittedBy ? formatTimestamp(voterResponse.pollResponse.submittedAt) : ''}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {voterResponses.length === 0 && (
          <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
            {filters.candidateFilters.length > 0 
              ? 'No voters found matching the selected candidates' 
              : 'No voters found in this barangay'}
          </Typography>
        )}
      </Grid>
    </Grid>
  );
};

export default VoterPreferencePrecinctReport;
